/**
 * 共用 reference (lookup) 表 schema — REQ-20260418007。
 * 4 個實體（industries / payment_certs / payment_methods / interview_types）
 * 形狀相同，共用一份 schema。
 */
import { z } from 'zod';

export const ReferenceRowSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ReferenceRow = z.infer<typeof ReferenceRowSchema>;

export const CreateReferenceSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    // REQ-20260519 ERP Phase 1A:放寬接受中文(貨品單位用「支 / 片 / 令」這種),
    // 既有 reference 都是大寫英數,擴大不會 break 既有資料。
    .regex(/^[一-龥A-Z0-9_]+$/, 'code 僅允許中文 / 大寫英數 / 底線'),
  name: z.string().trim().min(1).max(30),
  displayOrder: z.coerce.number().int().min(0).max(9999).default(0),
  isActive: z.boolean().default(true),
});
export type CreateReferenceInput = z.infer<typeof CreateReferenceSchema>;

export const UpdateReferenceSchema = z.object({
  name: z.string().trim().min(1).max(30).optional(),
  displayOrder: z.coerce.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateReferenceInput = z.infer<typeof UpdateReferenceSchema>;

export const ReferenceListQuerySchema = z.object({
  all: z.coerce.boolean().default(false),
});
export type ReferenceListQuery = z.infer<typeof ReferenceListQuerySchema>;

export type ReferenceEntity =
  | 'industry'
  | 'paymentCert'
  | 'paymentMethod'
  | 'interviewType'
  | 'productCategory'
  | 'productUnit'
  | 'supplierCategory'
  | 'warehouse'
  | 'processingOption'
  | 'printColorOption'
  | 'artworkHandlingOption'
  | 'shippingMethodOption'
  | 'printSideOption'
  | 'purchaseCategory';

export const REFERENCE_ENTITIES: Record<
  string,
  { entity: ReferenceEntity; label: string; apiPath: string }
> = {
  industries: { entity: 'industry', label: '行業類別', apiPath: 'industries' },
  'payment-certs': { entity: 'paymentCert', label: '收款憑證', apiPath: 'payment-certs' },
  'payment-methods': {
    entity: 'paymentMethod',
    label: '收款方式',
    apiPath: 'payment-methods',
  },
  'interview-types': {
    entity: 'interviewType',
    label: '訪談類別',
    apiPath: 'interview-types',
  },
  // REQ-20260519 ERP Phase 1A
  'product-categories': {
    entity: 'productCategory',
    label: '貨品類別',
    apiPath: 'product-categories',
  },
  'product-units': {
    entity: 'productUnit',
    label: '貨品單位',
    apiPath: 'product-units',
  },
  'supplier-categories': {
    entity: 'supplierCategory',
    label: '廠商類別',
    apiPath: 'supplier-categories',
  },
  warehouses: { entity: 'warehouse', label: '倉庫', apiPath: 'warehouses' },
  // REQ-20260810001 藝品印刷訂單四個現場欄位的常用選項。
  // 這四個欄位存的仍是自由文字(可複選串接、也可手動輸入),清單只是建議來源,
  // 不是 FK —— 與 REQ-20260805002 讓單位欄讀 product_units 卻仍存文字同一個決策。
  'processing-options': {
    entity: 'processingOption',
    label: '印刷加工',
    apiPath: 'processing-options',
  },
  'print-color-options': {
    entity: 'printColorOption',
    label: '印刷顏色',
    apiPath: 'print-color-options',
  },
  'artwork-handling-options': {
    entity: 'artworkHandlingOption',
    label: '稿件處理',
    apiPath: 'artwork-handling-options',
  },
  'shipping-method-options': {
    entity: 'shippingMethodOption',
    label: '出貨方式',
    apiPath: 'shipping-method-options',
  },
  // REQ-20260810003 印刷面別(單面 / 雙面)—— 同樣是建議清單而不是 FK
  'print-side-options': {
    entity: 'printSideOption',
    label: '印刷面別',
    apiPath: 'print-side-options',
  },
  // REQ-20260814002 進貨類別。與上面幾張同樣不是 FK,但 PurchaseOrder.orderType 存的是
  // **code**(PAPER / OUTSOURCE)而不是 name —— 顯示時才拿 code 對照名稱。
  'purchase-categories': {
    entity: 'purchaseCategory',
    label: '進貨類別',
    apiPath: 'purchase-categories',
  },
};
